
import { supabase } from "@/integrations/supabase/client";
import { FamilyProfile } from "./types";

// Function to get the family profile ID
export async function getFamilyProfileId(userId: string): Promise<string | null> {
  try {
    // Using .single() was causing the error when there are multiple rows
    // Change to use .limit(1) and get the first result instead
    const { data, error } = await supabase
      .from('family_profiles')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    
    if (error) throw error;
    // Check if we have any data and return the first row's ID
    return data && data.length > 0 ? data[0].id : null;
  } catch (error) {
    console.error('Error getting family profile ID:', error);
    return null;
  }
}

// Save family profile to Supabase
export async function saveFamilyProfileToSupabase(
  profile: FamilyProfile, 
  userId: string
): Promise<string | null> {
  try {
    // First, check if family profile exists
    let familyProfileId = await getFamilyProfileId(userId);
    
    if (familyProfileId) {
      // Update existing profile
      await supabase
        .from('family_profiles')
        .update({
          mother_name: profile.motherName,
          father_name: profile.fatherName,
          prefer_metric_height: profile.preferMetricHeight ?? true,
          prefer_metric_weight: profile.preferMetricWeight ?? true,
          updated_at: new Date().toISOString()
        })
        .eq('id', familyProfileId);
    } else {
      // Create new profile
      const { data, error } = await supabase
        .from('family_profiles')
        .insert({
          user_id: userId,
          mother_name: profile.motherName,
          father_name: profile.fatherName,
          prefer_metric_height: profile.preferMetricHeight ?? true,
          prefer_metric_weight: profile.preferMetricWeight ?? true
        })
        .select('id')
        .single();
      
      if (error) throw error;
      familyProfileId = data.id;
    }
    
    // If we have a family profile ID, save siblings and grandparents
    if (familyProfileId && profile.siblings && profile.siblings.length > 0) {
      // Delete existing siblings
      await supabase
        .from('siblings')
        .delete()
        .eq('family_profile_id', familyProfileId);
      
      // Insert new siblings
      const siblingsToInsert = profile.siblings.map(sibling => ({
        family_profile_id: familyProfileId,
        name: sibling.name,
        birthday: sibling.birthday
      }));
      
      await supabase
        .from('siblings')
        .insert(siblingsToInsert);
    }
    
    if (familyProfileId && profile.grandparents && profile.grandparents.length > 0) {
      // Delete existing grandparents
      await supabase
        .from('grandparents')
        .delete()
        .eq('family_profile_id', familyProfileId);
      
      // Insert new grandparents
      const grandparentsToInsert = profile.grandparents.map(grandparent => ({
        family_profile_id: familyProfileId,
        name: grandparent.name,
        relation: grandparent.relation
      }));
      
      await supabase
        .from('grandparents')
        .insert(grandparentsToInsert);
    }
    
    return familyProfileId;
  } catch (error) {
    console.error('Error saving family profile to Supabase:', error);
    return null;
  }
}

// Get family profile from Supabase
export async function getFamilyProfileFromSupabase(userId: string): Promise<FamilyProfile | null> {
  try {
    const { data: supabaseProfile, error } = await getFamilyProfileFromSupabaseRaw(userId);
    
    if (supabaseProfile && !error) {
      return supabaseProfile;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching family profile from Supabase:', error);
    return null;
  }
}

// Get raw data from Supabase without any transformation
export async function getFamilyProfileFromSupabaseRaw(userId: string) {
  try {
    return await import('@/utils/supabase/profiles').then(module => 
      module.getFamilyProfileFromSupabase(userId)
    );
  } catch (error) {
    console.error('Error importing and calling getFamilyProfileFromSupabase:', error);
    return { data: null, error };
  }
}
